/**
 * SelectionCyclingBadge — DOM cursor adornment that appears next to
 * the cursor when ≥2 entities sit under the pickbox (B12).
 *
 * AutoCAD parity: when SELECTIONCYCLING is non-zero AND the cursor
 * sits over a stacked region, AutoCAD draws a small "▦" / two-square
 * stack icon next to the pointer. This visual cue tells the user a
 * single-click will open the SELECTIONCYCLING dialog (when bit 2 is
 * set) or that Shift+Space cycles through the candidates.
 *
 * The badge is a pure DOM element (sibling of the canvas) — drawing
 * it via THREE would tie its position to the world coordinate system
 * and require per-frame projection. Following cursor coords in CSS
 * pixels is simpler and matches AutoCAD which renders the badge in
 * window coords through GDI+, not the GL viewport.
 *
 * Visibility rules (driven by props the host computes):
 *   visible = (SELECTIONCYCLING ≠ 0) AND (stackedCandidates.length ≥ 2)
 *           AND (cursor is on canvas)
 */

import * as React from "react";

import { useT } from "../../i18n";

export interface SelectionCyclingBadgeProps {
  /** Cursor position in CSS px relative to the workspace. */
  readonly cursorClient: { x: number; y: number } | null;
  /** Whether to render the badge. */
  readonly visible: boolean;
  /** Candidate count — drives the optional numeric superscript. */
  readonly count: number;
}

/**
 * The badge glyph — two stacked squares (▦) drawn via CSS rather than
 * a unicode character so it scales crisply on Hi-DPI displays.
 */
function BadgeGlyph(): React.ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <rect
        x="1"
        y="3"
        width="8"
        height="8"
        fill="rgba(255,255,255,0.95)"
        stroke="rgba(0,0,0,0.85)"
        strokeWidth="1"
      />
      <rect
        x="5"
        y="0"
        width="8"
        height="8"
        fill="rgba(255,255,255,0.95)"
        stroke="rgba(0,0,0,0.85)"
        strokeWidth="1"
      />
    </svg>
  );
}

export function SelectionCyclingBadge(
  props: SelectionCyclingBadgeProps,
): React.ReactElement | null {
  const t = useT();
  if (!props.visible || !props.cursorClient) return null;

  // Offset 16 px right + 6 px down — same pattern as SnapTip so the
  // badge doesn't overlap the OSNAP marker when both are visible at
  // the same time.
  const left = props.cursorClient.x + 16;
  const top = props.cursorClient.y + 6;

  return (
    <div
      data-testid="selection-cycling-badge"
      role="img"
      aria-label={t("selection_cycling.badge_label")}
      style={{
        position: "absolute",
        left,
        top,
        zIndex: 5,
        pointerEvents: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: "rgba(20, 20, 20, 0.85)",
        color: "#f0f0f0",
        padding: "2px 4px",
        borderRadius: 3,
        fontSize: 10,
        fontFamily: "system-ui, sans-serif",
        lineHeight: 1,
      }}
    >
      <BadgeGlyph />
      {props.count > 0 ? (
        <span
          data-testid="selection-cycling-badge-count"
          style={{ fontWeight: 600 }}
        >
          {props.count}
        </span>
      ) : null}
    </div>
  );
}
